import { useEffect, useRef } from 'react';
import {
  ADMIN_SLUG,
  isLockedProduct,
  isWheelRimCategory,
  allowsMultipleSelection
} from '../constants.js';
import { createProductKey } from '../AppUtils.js';

export const useInitialization = ({
  hasAccess,
  fetchProducts,
  setStoredBaseConfig,
  initializeWithLockedProducts,
  setPreselectedProducts,
  initializeCompulsoryData
}) => {
  useEffect(() => {
    if (hasAccess) {
      const initializeData = async () => {
        const { baseConfig, groupedProducts, categoryConfig } = await fetchProducts();
        setStoredBaseConfig(baseConfig);

        if (baseConfig && initializeCompulsoryData) {
          initializeCompulsoryData(baseConfig);
        }

        const params = new URLSearchParams(window.location.search);
        const slug = params.get('slug');
        const buildParam = params.get('b');

        if (slug && slug.toLowerCase() !== ADMIN_SLUG && !buildParam && groupedProducts && Object.keys(groupedProducts).length > 0) {
          initializeWithLockedProducts(groupedProducts);
        }

        // Load build from URL parameter
        if (buildParam) {
          setTimeout(() => {
            const buildSlugs = buildParam.split(',').filter(slug => slug.trim());
            const urlSelectedProducts = {};

            buildSlugs.forEach(slug => {
              let foundProduct = null;

              for (const mainCategory of Object.keys(groupedProducts)) {
                for (const subCategory of Object.keys(groupedProducts[mainCategory])) {
                  const found = groupedProducts[mainCategory][subCategory].find(
                    product => product.slug === slug
                  );
                  if (found) {
                    foundProduct = {
                      ...found,
                      wasAutoAdded: false
                    };
                    break;
                  }
                }
                if (foundProduct) break;
              }

              if (foundProduct) {
                // FIX: Check for ALL multi-select categories, not just wheel rims
                const isWheelRim = isWheelRimCategory(foundProduct.subCategory);
                const allowsMultiple = isWheelRim ||
                  allowsMultipleSelection(foundProduct.subCategory, categoryConfig) ||
                  allowsMultipleSelection(foundProduct.mainCategory, categoryConfig);

                const categoryKey = allowsMultiple
                  ? `${createProductKey(foundProduct.mainCategory, foundProduct.subCategory)}_${foundProduct.id}`
                  : createProductKey(foundProduct.mainCategory, foundProduct.subCategory);

                urlSelectedProducts[categoryKey] = foundProduct;
              }
            });

            if (Object.keys(urlSelectedProducts).length > 0) {
              setPreselectedProducts(urlSelectedProducts);
            }
          }, 100);
        }
        // Load default modules from base config
        else if (baseConfig?.defaultModules && slug && slug.toLowerCase() !== ADMIN_SLUG) {
          const preSelected = {};
          baseConfig.defaultModules.forEach(module => {
            let actualProduct = null;

            for (const mainCategory of Object.keys(groupedProducts)) {
              for (const subCategory of Object.keys(groupedProducts[mainCategory])) {
                const found = groupedProducts[mainCategory][subCategory].find(p =>
                  p.slug === module.slug || p.id === module._id
                );
                if (found) {
                  actualProduct = {
                    ...found,
                    wasAutoAdded: false,
                    isLocked: isLockedProduct(null, mainCategory, subCategory)
                  };
                  break;
                }
              }
              if (actualProduct) break;
            }

            if (actualProduct) {
              // FIX: Check for ALL multi-select categories, not just wheel rims
              const isWheelRim = isWheelRimCategory(actualProduct.subCategory);
              const allowsMultiple = isWheelRim ||
                allowsMultipleSelection(actualProduct.subCategory, categoryConfig) ||
                allowsMultipleSelection(actualProduct.mainCategory, categoryConfig);

              const key = allowsMultiple
                ? `${createProductKey(actualProduct.mainCategory, actualProduct.subCategory)}_${actualProduct.id}`
                : createProductKey(actualProduct.mainCategory, actualProduct.subCategory);

              preSelected[key] = actualProduct;
            }
          });

          setPreselectedProducts(preSelected);
        }
      };

      initializeData();
    }
  }, [hasAccess, fetchProducts, setStoredBaseConfig, initializeWithLockedProducts, setPreselectedProducts, initializeCompulsoryData]);
};

export const useProductIndexSync = ({
  hasAccess,
  selectedMainCategory,
  selectedSubCategory,
  productInBuild,
  viewState,
  productData,
  sortProductsByCompatibility,
  selectedProducts,
  setSelectedProduct
}) => {
  const categoryRef = useRef('');

  useEffect(() => {
    if (hasAccess) {
      const currentCategoryKey = `${selectedMainCategory}-${selectedSubCategory}`;

      if (categoryRef.current !== currentCategoryKey) {
        categoryRef.current = currentCategoryKey;

        if (productInBuild) {
          if (viewState === 'main' || !selectedMainCategory || !selectedSubCategory) {
            setSelectedProduct(0);
            return;
          }

          const products = productData[selectedMainCategory]?.[selectedSubCategory] || [];
          const sortedProducts = sortProductsByCompatibility(products, selectedProducts);
          const productIndex = sortedProducts.findIndex(p => p.id === productInBuild.id);
          setSelectedProduct(productIndex !== -1 ? productIndex : 0);
        } else {
          setSelectedProduct(0);
        }
      }
    }
  }, [hasAccess, selectedMainCategory, selectedSubCategory, productInBuild, viewState, productData, sortProductsByCompatibility, selectedProducts, setSelectedProduct]);
};